const responseCache = new Map();
const inFlightRequests = new Map();

// Server responses must never be shared between tenants in the same browser
// session (for example, while a Super Admin switches schools).
export const getTenantCacheKey = (resourceKey) => {
  let tenantId = "";
  try {
    tenantId = window.localStorage.getItem("activeTenantId") || "";
  } catch {
    // A missing browser storage implementation should not break a request.
  }
  return `tenant:${tenantId || "current"}:${resourceKey}`;
};

const isFresh = (entry) => entry && entry.expiresAt > Date.now();

export const getCachedOrFetch = async (
  key,
  fetcher,
  { ttlMs = 120000, forceRefresh = false } = {}
) => {
  if (!key || typeof fetcher !== "function") {
    throw new Error("getCachedOrFetch requires a cache key and fetcher function.");
  }

  if (!forceRefresh) {
    const cached = responseCache.get(key);
    if (isFresh(cached)) {
      return cached.value;
    }

    if (inFlightRequests.has(key)) {
      return inFlightRequests.get(key);
    }
  }

  const requestPromise = Promise.resolve()
    .then(() => fetcher())
    .then((value) => {
      responseCache.set(key, {
        value,
        expiresAt: Date.now() + Math.max(0, Number(ttlMs) || 0),
      });
      return value;
    })
    .finally(() => {
      inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, requestPromise);
  return requestPromise;
};

export const invalidateCache = (keyOrPrefix, { prefix = false } = {}) => {
  if (!keyOrPrefix) return;

  if (!prefix) {
    responseCache.delete(keyOrPrefix);
    inFlightRequests.delete(keyOrPrefix);
    return;
  }

  Array.from(responseCache.keys()).forEach((key) => {
    if (key.startsWith(keyOrPrefix)) {
      responseCache.delete(key);
    }
  });

  Array.from(inFlightRequests.keys()).forEach((key) => {
    if (key.startsWith(keyOrPrefix)) {
      inFlightRequests.delete(key);
    }
  });
};

export const clearAllCaches = () => {
  responseCache.clear();
  inFlightRequests.clear();
};
